// Description: Modal that will allow the user to test the template
// Updated: 08/08/2025
// Author: Simon Franklin

// React
import React, { useEffect, useState } from "react";

// Material UI Components
import { Box, styled, Button, Dialog, useMediaQuery, CircularProgress, MenuItem, TextField, Select, Typography, Tooltip } from "@mui/material";

// Components and functions from the project
import Titles from "../Titles/Titles";
import themeColors from "../ThemeColors/ThemeColors";
import DynamicButton from "../DynamicButton/DynamicButton";
import use123zapStore from "../../context/123zapStore";
import { Channels } from "../../api/aws/Channels/Channels";
import { insertMaskForPhoneNumber, notify } from "../../utils/utils";
import { CloseOutlined } from "@mui/icons-material";
import StatusIcon from "../StatusLabel/StatusIcon";

// Customizating the Dialog Component
const CustomDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiPaper-root": {
        borderRadius: "12px",
        width: "100%",
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxWidth: "500px !important",
    },
    "& .MuiDialog-container": {
        height: "100vh",
        width: "100vw",
        position: "fixed",
        top: "0",
        left: "0",
        display: "block",
    },
}));

// Render
const TestTemplateModal = ({ onClose, onConfirm, isOpen, wppNumber, setWppNumber, channelType }) => {

    // State variables
    const isSmallScreen = useMediaQuery("(max-width: 600px)");
    const userInfo = use123zapStore((state) => state.userInfo);

    const [selectedChannel, setSelectedChannel] = useState(null);
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wppAPiNumber, setWppApiNumber] = useState("");

    const [verified, setVerified] = useState(channelType === "WhatsAppAPI" ? false : true)

    useEffect(() => {
        (async () => {
            if (userInfo["UUID"]) {

                setLoading(true);
                await Channels().then((response) => {
                    const whatsAppChannels = response.channels
                        .filter(item => String(item.integration).includes("WhatsApp"))
                        .map(item => ({
                            id: item.code,
                            title: item.channel_identifier
                        }));

                    whatsAppChannels.unshift({ id: false, title: 'Selecione seu canal' });

                    setSelectedChannel(false);
                    setChannels(whatsAppChannels);

                    const wppChannel = response.channels.find((item) => item.integration === "WhatsApp");

                    if (wppChannel) {
                        setWppApiNumber(wppChannel.channel_identifier);
                    }

                }).finally(() => setLoading(false));

            };

        })();

    }, [userInfo])

    const closeModal = () => {
        onClose(false);
    };

    const handleWhatsAppClick = () => {
        if (wppAPiNumber === "") {
            notify("Error", "Dados para testar o canal não foram encontrados!", true);
            return;
        }

        setVerified(true);

        const url = `https://api.whatsapp.com/send/?phone=${wppAPiNumber}&text=Esta+mensagem+deve+ser+encaminhada+para+dar+início+à+janela+de+24+horas+estabelecida+pela+meta.&type=phone_number&app_absent=0`;
        window.open(url, "_blank");
    };

    return (
        <CustomDialog
            open={isOpen}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth={false}
            onBackdropClick={closeModal}
        >

            <div onClick={closeModal} style={{ cursor: 'pointer' }}>
                <CloseOutlined
                    sx={{
                        color: themeColors.gray40,
                        display: 'block',
                        position: 'absolute',
                        top: { xs: '10px', sm: '30px' },
                        right: { xs: '10px', sm: '30px' },
                    }}
                />
            </div>
            {
                loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "400px", height: "400px" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        padding={"40px"}
                        sx={{ textAlign: "center", backgroundColor: "#fff" }}
                    >
                        <Box
                            display={"flex"}
                            sx={{
                                flexDirection: "column",
                                gap: "10px",
                                marginBottom: "20px",
                            }}
                        >
                            <Titles
                                font={"Satoshi"}
                                children={"Envie uma mensagem"}
                                color={themeColors.purple600}
                                type={isSmallScreen ? "H4" : "H3"}
                                styles={{ fontWeight: 500 }}
                            />

                            <Titles
                                font={"Satoshi"}
                                children={verified === true ? <p>Aqui você pode enviar uma mensagem de teste. As variáveis serão <strong style={{ color: "#000" }}>substituídas por valores de exemplo</strong></p> : <p>Antes de testar o envio pelo canal de WhatsApp API, é preciso enviar um "Oi" para o seu canal cadastrado.</p>}
                                color={themeColors.gray50}
                                type={"P1"}
                            />

                            {
                                verified === false && (
                                    <Box
                                        onClick={handleWhatsAppClick}
                                        sx={{
                                            backgroundColor: themeColors.green01,
                                            color: themeColors.green03,
                                            padding: "6px 10px",
                                            borderRadius: "6px",
                                            border: "none",
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "10px",
                                            cursor: "pointer"
                                        }}>
                                        <StatusIcon status={"Alert"} color={themeColors.green03} />
                                        <Typography sx={{ fontSize: "16px" }}><span style={{ fontWeight: 600, textDecoration: "none", color: "inherit", display: "inline-block" }}>Clique aqui</span> para realizar esse passo.</Typography>
                                    </Box>
                                )
                            }
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", marginBottom: "10px", gap: "15px" }}>
                            {/* <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "start" }}>
                                <Titles children={"Qual será o canal de saída?"} color={"#000"} type={"P1"} />
                                <Select
                                    name={"output-channel"}
                                    sx={{
                                        background: "#f9f9f9",
                                        borderRadius: "6px",
                                        minWidth: "100%",
                                        ".css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input": {
                                            padding: "13px",
                                        },
                                        textAlign: "start"
                                    }}
                                    onChange={(e) => setSelectedChannel(e.target.value)}
                                    value={selectedChannel}
                                >
                                    {
                                        channels.map((option, index) => (
                                            <MenuItem key={index} value={option.id}>
                                                {option.title}
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </Box> */}
                            {
                                channelType === "SMS" || channelType.includes("WhatsApp") ? (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "start" }}>
                                        <Titles children={"Informe seu número de WhatsApp"} color={"#000"} type={"P1"} />
                                        <TextField
                                            name={"wpp_number"}
                                            variant="outlined"
                                            placeholder={"(99) 9999-9999"}
                                            value={insertMaskForPhoneNumber(String(wppNumber))}
                                            onChange={(e) => setWppNumber(e.target.value)}
                                            rows={1}
                                            fullWidth
                                            sx={{ background: '#F9F9F9', borderRadius: '6px', '.css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input': { padding: '13px' } }}
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "start" }}>
                                        <Titles children={"Informe seu E-mail"} color={"#000"} type={"P1"} />
                                        <TextField
                                            name={"wpp_number"}
                                            variant="outlined"
                                            placeholder={"exemplo@email.com"}
                                            value={wppNumber}
                                            type="email"
                                            onChange={(e) => setWppNumber(e.target.value)}
                                            rows={1}
                                            fullWidth
                                            sx={{ background: '#F9F9F9', borderRadius: '6px', '.css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input': { padding: '13px' } }}
                                        />
                                    </Box>
                                )
                            }
                        </Box>

                        <Box display={"flex"} sx={{ flexDirection: "column", gap: "10px", marginTop: "20px" }}>
                            <Button
                                sx={{
                                    backgroundColor: "transparent",
                                    border: `2px solid ${themeColors.purple400}`,
                                    color: themeColors.purple500,
                                    padding: "12px 20px",
                                    borderRadius: "6px",
                                    width: "100%",
                                }}
                                onClick={closeModal}
                            >
                                Fechar
                            </Button>
                            {
                                verified === true ? (
                                    <DynamicButton
                                        text={"Testar Envio"}
                                        loadingText={"Aguarde..."}
                                        style={{
                                            backgroundColor: themeColors.purple500,
                                            color: "white",
                                            padding: "12px 20px",
                                            borderRadius: "6px",
                                            border: "none",
                                            width: "100%",
                                            "&:hover": {
                                                backgroundColor: themeColors.purple400,
                                            },
                                        }}
                                        handleFunction={async () => {
                                            if (wppNumber === "") {
                                                notify("Alert", "Preencha o número de WhatsApp!", true);
                                                return;
                                            }

                                            await onConfirm();
                                        }}
                                        variant={"contained"}
                                    />
                                ) : (
                                    <Tooltip title={<Typography variant="body2">Antes de testar o envio de mensagem, é necessário abrir o canal cadastrado.</Typography>} arrow placement="bottom" >
                                        <span style={{ width: "100%" }}>
                                            <DynamicButton
                                                text={"Testar Envio"}
                                                loadingText={"Aguarde..."}
                                                style={{
                                                    backgroundColor: themeColors.purple500,
                                                    color: "white",
                                                    padding: "12px 20px",
                                                    borderRadius: "6px",
                                                    border: "none",
                                                    width: "100%"
                                                }}
                                                handleFunction={() => {}}
                                                variant={"contained"}
                                            />
                                        </span>
                                    </Tooltip>
                                )
                            }
                        </Box>
                    </Box>
                )
            }
        </CustomDialog>
    )
}

export default TestTemplateModal